"use client";

import { useTranslations } from "next-intl";

const SPEC_ITEMS = [
	{ labelKey: "techSpecsDimensionalAccuracyLabel", valueKey: "techSpecsDimensionalAccuracyValue" },
	{ labelKey: "techSpecsSurfaceFinishLabel", valueKey: "techSpecsSurfaceFinishValue" },
	{ labelKey: "techSpecsMinWallThicknessLabel", valueKey: "techSpecsMinWallThicknessValue" },
	{ labelKey: "techSpecsMinInnerHoleDiameterLabel", valueKey: "techSpecsMinInnerHoleDiameterValue" },
	{ labelKey: "techSpecsDimensionalToleranceLabel", valueKey: "techSpecsDimensionalToleranceValue" },
	{ labelKey: "techSpecsWeightRangeLabel", valueKey: "techSpecsWeightRangeValue" },
	{ labelKey: "techSpecsMaxPartDimensionLabel", valueKey: "techSpecsMaxPartDimensionValue" },
	{ labelKey: "techSpecsBatchCapacityLabel", valueKey: "techSpecsBatchCapacityValue" },
] as const;

const MATERIAL_ROWS = [
	{ prefix: "materialPerfRow1" },
	{ prefix: "materialPerfRow2" },
	{ prefix: "materialPerfRow3" },
	{ prefix: "materialPerfRow4" },
	{ prefix: "materialPerfRow5" },
] as const;

const MATERIAL_COLS = ["Material", "MeltingTemp", "TensileStrength", "Hardness", "Process"] as const;

const PROCESS_CARDS = [
	{ prefix: "processParamsCard1", color: "#64a525" },
	{ prefix: "processParamsCard2", color: "#64a525" },
	{ prefix: "processParamsCard3", color: "#64a525" },
] as const;

const PROCESS_FIELDS = ["MinWall", "Surface", "Accuracy", "Shell", "LeadTime"] as const;

export default function InvestmentCastingTechnicalParameters() {
	const t = useTranslations("new-investment-casting");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* Section header */}
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("techParamsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("techParamsDesc")}</p>
				</header>

				{/* General Technical Specifications */}
				<div className="mb-10 md:mb-14 lg:mb-16">
					<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
						{t("techSpecsTitle")}
					</h3>
					<div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4">
						{SPEC_ITEMS.map((item) => (
							<div
								key={item.labelKey}
								className="flex flex-col items-center justify-center bg-white px-4 py-5 text-center sm:py-6"
							>
								<p className="mb-1 text-xs text-gray-500 sm:text-sm">{t(item.labelKey)}</p>
								<p className="text-base font-bold text-black sm:text-lg">{t(item.valueKey)}</p>
							</div>
						))}
					</div>
				</div>

				{/* Material Performance Table */}
				<div className="mb-10 md:mb-14 lg:mb-16">
					<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
						{t("materialPerfTitle")}
					</h3>

					{/* Desktop table */}
					<div className="hidden overflow-x-auto md:block">
						<table className="w-full border-collapse text-sm text-black">
							<thead>
								<tr>
									{MATERIAL_COLS.map((col) => (
										<th
											key={col}
											className="border-b-2 border-[#64a525] bg-[#64a525] px-4 py-5 text-left text-sm font-bold text-white lg:text-base"
										>
											{t(`materialPerfCol${col}`)}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{MATERIAL_ROWS.map((row, rowIdx) => (
									<tr key={row.prefix}>
										{MATERIAL_COLS.map((col) => (
											<td
												key={col}
												className={`border-b border-[#dbdbdb] px-4 py-8 align-top ${
													col === "Material" ? "font-bold" : ""
												}`}
											>
												{t(`${row.prefix}${col}`)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Mobile cards */}
					<div className="flex flex-col gap-4 md:hidden">
						{MATERIAL_ROWS.map((row) => (
							<div key={row.prefix} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
								<div className="bg-[#64a525] px-4 py-3">
									<h4 className="text-sm font-bold text-white sm:text-base">{t(`${row.prefix}Material`)}</h4>
								</div>
								<div className="grid grid-cols-2 gap-px bg-gray-100">
									{MATERIAL_COLS.slice(1).map((col) => (
										<div key={col} className="bg-white px-4 py-3">
											<p className="mb-1 text-xs font-semibold text-gray-500">{t(`materialPerfCol${col}`)}</p>
											<p className="text-sm leading-relaxed text-black">{t(`${row.prefix}${col}`)}</p>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Three Process Parameters */}
				<div>
					<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
						{t("processParamsTitle")}
					</h3>
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{PROCESS_CARDS.map((card) => (
							<div key={card.prefix} className="overflow-hidden rounded-xl bg-[#f6ffde] pb-4">
								<div className="px-6 pb-4 pt-10">
									<h4 className="text-center text-base font-bold text-[#64a525] sm:text-lg">
										{t(`${card.prefix}Title`)}
									</h4>
								</div>
								<div className="divide-y divide-[#d3d6ca] px-5 py-2">
									{PROCESS_FIELDS.map((field) => (
										<div key={field} className="flex items-center justify-between py-3">
											<span className="text-xs text-gray-600 sm:text-sm">
												{t(`${card.prefix}${field}Label`)}
											</span>
											<span className="text-right text-xs font-semibold text-black sm:text-sm">
												{t(`${card.prefix}${field}Value`)}
											</span>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
